import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress } from '@material-ui/core';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import CloudDownloadOutlinedIcon from '@material-ui/icons/CloudDownloadOutlined';
import { useDispatch } from 'react-redux';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { exportAnalyticsReport } from 'slices/AnalyticsSlice';
import styles from './AnalyticsReportExportButton.module.scss';

export function downloadAnalyticsCsv({ content, fileName, mimeType }) {
  const blob = new Blob([content], { type: mimeType || 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName || 'kea-analytics.csv';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export default function AnalyticsReportExportButton({ shopId, reportType, disabled }) {
  const dispatch = useDispatch();
  const t = useLocalizedMessage();
  const [status, setStatus] = useState('idle');
  const resetTimer = useRef(null);

  useEffect(() => () => {
    if (resetTimer.current) window.clearTimeout(resetTimer.current);
  }, []);

  const handleExport = async () => {
    if (!shopId || status === 'loading') return;
    setStatus('loading');
    try {
      const result = await dispatch(exportAnalyticsReport({ shopId, reportType })).unwrap();
      downloadAnalyticsCsv(result);
      setStatus('success');
      resetTimer.current = window.setTimeout(() => setStatus('idle'), 1800);
    } catch (_error) {
      setStatus('idle');
    }
  };

  const isLoading = status === 'loading';
  const isSuccess = status === 'success';
  const label = isLoading
    ? t('analytics.export.preparing')
    : (isSuccess ? t('analytics.export.downloaded') : t('analytics.export.button'));

  return (
    <Button
      aria-busy={isLoading}
      className={`${styles.button} ${isSuccess ? styles.success : ''}`.trim()}
      disabled={disabled || isLoading || !shopId}
      onClick={handleExport}
      startIcon={isLoading
        ? <CircularProgress aria-hidden="true" color="inherit" size={16} thickness={4.5} />
        : (isSuccess ? <CheckRoundedIcon aria-hidden="true" /> : <CloudDownloadOutlinedIcon aria-hidden="true" />)}
      variant="outlined"
    >
      <span aria-live="polite">{label}</span>
    </Button>
  );
}

AnalyticsReportExportButton.propTypes = {
  shopId: PropTypes.string,
  reportType: PropTypes.oneOf(['top_queries', 'zero_results', 'lost_opportunities']).isRequired,
  disabled: PropTypes.bool,
};

AnalyticsReportExportButton.defaultProps = {
  shopId: null,
  disabled: false,
};
